import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SystemStatusHeader = () => {
  const [autoRefresh, setAutoRefresh] = useState('30s');
  const [alertsSilenced, setAlertsSilenced] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const refreshIntervals = [
    { value: '30s', label: '30 seconds' },
    { value: '1min', label: '1 minute' },
    { value: '5min', label: '5 minutes' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdated(new Date());
    }, autoRefresh === '30s' ? 30000 : autoRefresh === '1min' ? 60000 : 300000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const systemStatus = {
    status: 'healthy',
    uptime: '99.97%',
    activeIncidents: 0,
    lastIncident: '2 days ago'
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-success';
      case 'warning': return 'text-warning';
      case 'critical': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return 'CheckCircle';
      case 'warning': return 'AlertTriangle';
      case 'critical': return 'XCircle';
      default: return 'Circle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        {/* System Status */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Icon 
              name={getStatusIcon(systemStatus?.status)} 
              size={24} 
              className={getStatusColor(systemStatus?.status)}
            />
            <div>
              <h2 className="text-lg font-semibold text-foreground">System Status</h2>
              <p className="text-sm text-muted-foreground">
                Uptime: {systemStatus?.uptime} • Last incident: {systemStatus?.lastIncident}
              </p>
            </div>
          </div>
          
          {systemStatus?.activeIncidents > 0 && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-error/10 text-error rounded-md">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm font-medium">{systemStatus?.activeIncidents} Active</span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center space-x-4">
          {/* Auto Refresh */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Auto-refresh:</span>
            <div className="flex bg-muted rounded-md p-1">
              {refreshIntervals?.map((interval) => (
                <button
                  key={interval?.value}
                  onClick={() => setAutoRefresh(interval?.value)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-hover ${
                    autoRefresh === interval?.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {interval?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Alert Silence Toggle */}
          <Button
            variant={alertsSilenced ? "destructive" : "outline"}
            size="sm"
            onClick={() => setAlertsSilenced(!alertsSilenced)}
            iconName={alertsSilenced ? "BellOff" : "Bell"}
            iconPosition="left"
          >
            {alertsSilenced ? 'Alerts Silenced' : 'Silence Alerts'}
          </Button>

          {/* Manual Refresh */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLastUpdated(new Date())}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>
      </div>
      {/* Last Updated */}
      <div className="mt-4 pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground">
          Last updated: {lastUpdated?.toLocaleTimeString()} • Next refresh in {autoRefresh}
        </p>
      </div>
    </div>
  );
};

export default SystemStatusHeader;